"use client";

/**
 * Full citation detail panel for the right rail.
 *
 * Extracted from `compass-intake.jsx:844-952` (`.cs-detail`, `.cs-dtop`,
 * `.cs-dval`, `.cs-dlabel`, `.cs-pill`, `.cs-tabs` classes).
 */

import { useEffect, useRef, useState } from "react";
import { InfoDot } from "../data-display/InfoDot";
import type { SrcCardSource } from "../data-display/SrcCard";
import { SrcCard } from "../data-display/SrcCard";
import { Alert, Check, Clock, Pen } from "../lib/icons";
import type { ProvenanceType } from "../lib/provenance";
import { PROV_META, TYPE_INFO } from "../lib/provenance";
import type { HistoryStage } from "./CiteHistory";
import { CiteHistory } from "./CiteHistory";

/* ---------- types ---------- */

export interface CitationData {
  value?: string;
  label: string;
  type: ProvenanceType;
  by?: string;
  note?: string;
  estimate?: string;
  sources?: SrcCardSource[];
  historyCount?: number;
  snippet?: string;
}

export interface CiteDetailProps {
  /** Citation data. */
  citation: CitationData;
  /** Whether value has been edited. */
  edit?: boolean;
  /**
   * The AI-generated value that existed before the user edited it.
   * Required when `edit=true` so the History tab can show the distinct
   * "Generated at <priorValue>" entry instead of re-using `citation.value`.
   */
  priorValue?: string;
  /** Currently active tab. */
  tab: "source" | "history";
  /** Tab setter. */
  setTab?: (tab: "source" | "history") => void;
  /** Close handler. */
  onClose?: () => void;
  /** Save handler. */
  onSave?: (value: string) => void;
  /** Show stale value warning. */
  showStale?: boolean;
  /** Whether stale was resolved. */
  staleResolved?: boolean;
  /** Keep-stale handler. */
  onKeepStale?: () => void;
  /** Auto-open in editing mode. */
  defaultEditing?: boolean;
}

/* ---------- pill color map ---------- */

const PILL_BG: Record<string, string> = {
  prov: "bg-(--prov)",
  est: "bg-(--est)",
  cat: "bg-(--accent-plum)",
  empty: "bg-(--empty)",
};

/**
 * Full citation detail panel — value + label, provenance pill,
 * source/history tabs, quote block, and source cards.
 */
export function CiteDetail({
  citation: c,
  edit = false,
  priorValue,
  tab,
  setTab,
  onClose: _onClose,
  onSave,
  showStale = false,
  staleResolved = false,
  onKeepStale,
  defaultEditing = false,
}: CiteDetailProps) {
  const [editing, setEditing] = useState(defaultEditing);
  const [draft, setDraft] = useState(c.value ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const normalizedType = (c.type === "vault" ? "catalog" : c.type) as Exclude<
    ProvenanceType,
    "vault"
  >;
  const et = edit ? ("provided" as const) : normalizedType;
  const meta = PROV_META[et] ?? PROV_META.estimated;
  const histCount = (c.historyCount ?? 0) + (edit ? 1 : 0);

  function startEdit() {
    setDraft(c.value ?? "");
    setEditing(true);
  }

  function save() {
    const v = draft.trim();
    if (!v) return;
    onSave?.(v);
    setEditing(false);
  }

  /* Build history stages for the history tab */
  const historyStages: HistoryStage[] = edit
    ? [
        {
          type: "provided",
          head: (
            <>
              Edited to <b>{c.value}</b> by you
            </>
          ),
          meta: "This opportunity · just now",
          refs: [],
        },
        {
          type: normalizedType,
          head: (
            <>
              Generated at <b>{priorValue ?? c.value}</b> by Compass
            </>
          ),
          meta: "This opportunity · earlier",
          prior: true,
          refs: c.sources,
        },
      ]
    : [
        {
          type: normalizedType,
          head:
            c.type === "provided" ? (
              <>
                Set to <b>{c.value}</b> by {c.by}
              </>
            ) : (
              <>
                Generated
                {c.value ? (
                  <>
                    {" "}
                    at <b>{c.value}</b>
                  </>
                ) : null}{" "}
                by Compass
              </>
            ),
          meta: "This opportunity",
          refs: c.sources,
        },
      ];

  return (
    <div className="p-[18px] pb-6 min-w-0 box-border w-full">
      {/* Top: value + label */}
      <div className="flex items-start">
        <div className="flex-1">
          {editing ? (
            <>
              <div className="text-[15px] text-(--fg-secondary) mb-[10px]">
                {c.label}
              </div>
              <div className="border border-(--border-default) rounded-(--radius-field) bg-(--bg-canvas) p-[13px]">
                <input
                  type="text"
                  className={[
                    "w-full text-[18px] font-bold text-(--fg-primary)",
                    "bg-(--bg-white) border border-(--border-default)",
                    "rounded-(--radius-sm) px-[11px] py-[10px]",
                    "outline-none",
                    "focus:border-(--fg-brand) focus:shadow-[inset_0_0_0_1px_var(--fg-brand)]",
                  ].join(" ")}
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") save();
                  }}
                />
                <div className="flex gap-[9px] mt-[11px]">
                  <button
                    type="button"
                    className="text-[13px] font-semibold bg-(--brand-navy) text-white border-0 rounded-(--radius-field) px-[14px] py-[9px] cursor-pointer hover:bg-(--steel-110) disabled:bg-(--border-default) disabled:text-(--fg-muted) disabled:cursor-default"
                    onClick={save}
                    disabled={!draft.trim()}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="text-[13px] font-semibold bg-(--bg-white) text-(--fg-secondary) border border-(--border-default) rounded-(--radius-field) px-[14px] py-[9px] cursor-pointer hover:bg-(--bg-canvas)"
                    onClick={() => setEditing(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="text-[34px] leading-[1.1] font-bold tracking-[-0.02em] text-(--fg-primary) flex items-center gap-[9px] overflow-wrap-anywhere">
                {c.value}
                <button
                  type="button"
                  className="bg-transparent border-0 cursor-pointer text-(--fg-muted) p-[2px] inline-flex align-middle hover:text-(--fg-primary)"
                  onClick={startEdit}
                  title="Override this value"
                >
                  <Pen size={16} />
                </button>
              </div>
              <div className="text-[15px] text-(--fg-secondary) mt-1 tracking-[-0.01em]">
                {c.label}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Provenance pill row */}
      <div className="flex items-center gap-[9px] mt-[14px]">
        <span
          className={[
            "inline-flex items-center gap-1.5",
            "rounded-full px-3 py-[5px] pl-[10px]",
            "text-[13px] font-semibold text-white",
            PILL_BG[meta.cls] ?? "bg-(--est)",
          ].join(" ")}
        >
          <meta.icon size={13} />
          {meta.label}
          {TYPE_INFO[et] && <InfoDot text={TYPE_INFO[et]} onDark />}
        </span>
      </div>

      {/* Source / History tabs */}
      <div className="flex gap-[22px] border-b border-(--border-subtle) mt-4">
        <button
          type="button"
          className={[
            "appearance-none bg-transparent border-0 pb-[9px] pt-[9px]",
            "text-[12px] tracking-[.07em] uppercase font-semibold",
            "cursor-pointer border-b-2 -mb-px",
            "flex items-center gap-1.5",
            tab === "source"
              ? "text-(--fg-primary) border-b-(--fg-brand)"
              : "text-(--fg-muted) border-b-transparent",
          ].join(" ")}
          onClick={() => setTab?.("source")}
        >
          Source
        </button>
        <button
          type="button"
          className={[
            "appearance-none bg-transparent border-0 pb-[9px] pt-[9px]",
            "text-[12px] tracking-[.07em] uppercase font-semibold",
            "cursor-pointer border-b-2 -mb-px",
            "flex items-center gap-1.5",
            tab === "history"
              ? "text-(--fg-primary) border-b-(--fg-brand)"
              : "text-(--fg-muted) border-b-transparent",
          ].join(" ")}
          onClick={() => setTab?.("history")}
        >
          <Clock size={14} />
          History
          {histCount > 0 && (
            <span className="text-(--fg-muted) font-medium">{histCount}</span>
          )}
        </button>
      </div>

      {/* Tab content */}
      {tab === "history" ? (
        <CiteHistory stages={historyStages} />
      ) : (
        <>
          {showStale && (
            <div className="mt-[14px]">
              {staleResolved ? (
                <div className="flex items-center gap-2 p-[10px_12px] bg-(--bg-canvas) border border-(--border-default) rounded-(--radius-field) text-[12px] leading-[1.45] text-(--fg-secondary)">
                  <span className="text-green-600 shrink-0">
                    <Check size={14} />
                  </span>
                  <span>Stale value resolved.</span>
                </div>
              ) : (
                <div className="p-[10px_12px] bg-(--stale-bg) border border-(--stale-border) rounded-(--radius-field)">
                  <div className="flex gap-2 items-start text-[12px] leading-[1.45] text-(--status-warning-text)">
                    <span className="text-(--status-warning-text) shrink-0 mt-px">
                      <Alert size={14} />
                    </span>
                    <span>
                      <b className="font-bold">Org profile has newer data.</b>{" "}
                      This value was sourced from an older org profile snapshot.
                      Update it now or keep the current value.
                    </span>
                  </div>
                  <div className="flex gap-[8px] mt-[10px]">
                    <button
                      type="button"
                      className="text-[12px] font-semibold bg-(--brand-navy) text-white border-0 rounded-(--radius-field) px-[12px] py-[7px] cursor-pointer hover:bg-(--steel-110)"
                      onClick={() => startEdit()}
                    >
                      Update value
                    </button>
                    <button
                      type="button"
                      className="text-[12px] font-semibold bg-(--bg-white) text-(--fg-secondary) border border-(--border-default) rounded-(--radius-field) px-[12px] py-[7px] cursor-pointer hover:bg-(--bg-canvas)"
                      onClick={onKeepStale}
                    >
                      Keep current
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {c.type === "provided" && c.note ? (
            <div className="bg-(--bg-canvas) border-l-[3px] border-l-(--border-default) rounded-r-(--radius-sm) py-3 px-[14px] mt-[14px] text-[13.5px] leading-[1.5] text-(--fg-primary)">
              &ldquo;{c.note}&rdquo;
            </div>
          ) : c.snippet ? (
            <div className="bg-(--bg-canvas) border-l-[3px] border-l-(--border-default) rounded-r-(--radius-sm) py-3 px-[14px] mt-[14px] text-[13.5px] leading-[1.5] text-(--fg-primary)">
              &ldquo;{c.snippet}&rdquo;
            </div>
          ) : c.estimate ? (
            <div className="text-[13.5px] leading-[1.55] text-(--fg-primary) mt-[14px] [&_b]:font-bold">
              {c.estimate}.
            </div>
          ) : null}

          {c.sources && c.sources.length > 0 && (
            <div className="mt-[13px] flex flex-col gap-2">
              {c.sources.map((s, i) => (
                <SrcCard
                  key={i}
                  source={s}
                  letter={String.fromCharCode(97 + i)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
